/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DeviceType } from '@squiz/dxp-console-support';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DXP_ACTIVE_CLASS } from '../../../Constants/Design';
import { CUSTOM_ICON_MENU } from '../../../Icons';
import { BUTTON_FORMAT_TRANSPARENT, BUTTON_THEME_DEFAULT, Button, ButtonProps } from '../../Button';
import { SdsTransition, SdsTransitionProps } from '../../SdsTransition';
import { SideNav } from '../SideNav';
import { SideNavLauncherProps } from './SideNavLauncher.props';

/**
 * SDS SideNav component launcher.
 */
export const SideNavLauncher = (props: SideNavLauncherProps): JSX.Element => {
  const { t } = useTranslation();
  const { classes, device, menuSections, ...rest } = props;
  const [activeClass, setActiveClass] = useState<string>(``);
  const [showMenuWrapper, setShowMenuWrapper] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const launchMenuClass = clsx(`sds-sidenav-launcher`, classes);

  // Labels
  const labels = useMemo(() => {
    return {
      openSideMenu: t(`Open side menu`),
    };
  }, [t]);

  // Device detection
  const isMobile = device === DeviceType.mobile;
  const isTablet = device === DeviceType.tablet;
  const isTabletSmall = device === DeviceType.tabletSmall;

  // Mobile main menu callback
  const toggleMobileMenu = useCallback((): void => {
    const newActive = activeClass === `` ? DXP_ACTIVE_CLASS : ``;
    setActiveClass(newActive);
  }, [activeClass, setActiveClass]);

  // Tablet main menu callback
  const toggleTabletMenu = useCallback((): void => {
    if (!showMenuWrapper) {
      setActiveClass(DXP_ACTIVE_CLASS);
      setShowMenuWrapper(true);
    } else {
      setShowMenuWrapper(false);
    }
  }, [setActiveClass, setShowMenuWrapper, showMenuWrapper]);

  const closeHandler = (): void => {
    setActiveClass(``);
  };

  useEffect(() => {
    const handleGeneralClick = (event: MouseEvent): void => {
      if (menuRef.current?.contains(event.target as Node)) {
        const parentItem = (event.target as HTMLElement).closest('.sds-sidenav-item');
        const hasChildItems = parentItem && (parentItem as HTMLLIElement).querySelector(`.sds-sidenav-item`);

        // Clicking on the parent menu item should reveal the sub menu items without closing the side nav.
        if (hasChildItems) {
          return;
        }

        const sidePanel = (event.target as HTMLElement).closest('.sds-sidenav');

        // Clicking the empty space of the side panel should do nothing.
        if (!parentItem && sidePanel) {
          return;
        }

        // The side nav of non-mobile layouts will experience the sliding transition.
        if (isMobile) {
          closeHandler();
        } else {
          setShowMenuWrapper(false);
        }
      }

      // Clicking outside of side nav
      const launcherButton = (event.target as HTMLElement).closest(`.sds-sidenav-launcher > button`) as HTMLElement;
      if (!launcherButton) {
        setShowMenuWrapper(false);
      }
    };

    document.addEventListener('click', handleGeneralClick);
    return (): void => document.removeEventListener('click', handleGeneralClick);
  }, [isMobile]);

  if (isMobile) {
    const buttonProps: ButtonProps = {
      classes: activeClass,
      format: BUTTON_FORMAT_TRANSPARENT,
      icon: CUSTOM_ICON_MENU,
      label: labels.openSideMenu,
      onClick: toggleMobileMenu,
      theme: BUTTON_THEME_DEFAULT,
    };

    return (
      <div className={launchMenuClass} {...rest}>
        <Button {...buttonProps} ref={buttonRef} />
        <>
          {activeClass && (
            <div className="sds-sidenav-mobile" ref={menuRef}>
              <SideNav sections={menuSections} />
              <div className="sds-sidenav-bg"></div>
            </div>
          )}
        </>
      </div>
    );
  }

  if (isTabletSmall || isTablet) {
    const buttonProps: ButtonProps = {
      classes: `button--toolbar button--menu ${activeClass}`,
      format: BUTTON_FORMAT_TRANSPARENT,
      icon: CUSTOM_ICON_MENU,
      label: labels.openSideMenu,
      onClick: toggleTabletMenu,
      theme: BUTTON_THEME_DEFAULT,
    };

    const tabletMenu = (
      <>
        {activeClass && (
          <div className="sds-sidenav-tablet" ref={menuRef}>
            <SideNav sections={menuSections} />
            <div className="sds-sidenav-bg"></div>
          </div>
        )}
      </>
    );

    const transitionProps = {
      exitCallback: closeHandler,
      isActive: showMenuWrapper,
    } as SdsTransitionProps;

    return (
      <div className={launchMenuClass} {...rest}>
        <Button {...buttonProps} ref={buttonRef} />
        <SdsTransition {...transitionProps}>{tabletMenu}</SdsTransition>
      </div>
    );
  }

  return <></>;
};
